/*
 *  -----------------------------------------------------------------------
 *    <copyright file="site-overview-scrollable-grid.helper.tsx" company="Triumph Research Intelligence">
 *        Copyright (C) 2023 by Triumph Research Intelligence. All rights reserved.
 *    </copyright>
 *   -----------------------------------------------------------------------
 *
 */

import { IExtendedDataSetFields } from '@components/central-monitoring/site-dashboard-summary-v2/site-dashboard-summary-v2.interface';
import {
    ColumnSummary,
    SiteRow,
} from '@components/central-monitoring/study-summary/site-overview-scrollable-grid/site-overview-scrollable-grid.interface';
import { getObjValueByPath } from '@components/central-monitoring/study-summary/study-overview-scrollable-grid/study-overview-scrollable-grid.helper';
import { createDatePipe, getFilterType } from '@components/central-monitoring/study-summary/study-summary.helper';
import { IExpandableGridColumnV2 } from '@components/core/expandable/grid-v2/interfaces-v2/grid-props-v2.interface';
import { GridColumnDateFilter, GridColumnMenuActiveFilter } from '@components/core/grid-filters';
import { useGridCells } from '@hooks/index';
import Constants from '@utils/constants';
import { convertYesNoToBoolean } from '@utils/convertor';
import { getCellNumberFromString, getChangeMovement } from '@utils/grid-v2';
import { isSplittedValue, isStringNumeric, validOverviewDate } from '@utils/validation';
import { FieldDefinitionFieldType } from '@types/graphql-global-types';

const { MaxLengthCell, MovementCell, CustomizableCell, SplittedValuesCell, DateCell, YesNoCell } = useGridCells();

const getFieldData = (col: ColumnSummary, fieldName: string, type?: string) => {
    const value = col[fieldName];

    if (isStringNumeric(value)) {
        return { [fieldName]: getCellNumberFromString(value, type) };
    }

    if (validOverviewDate(value)) {
        return { [fieldName]: createDatePipe(value) };
    }

    if (value !== null && type === Constants.FieldDefinitionFieldTextTypes.bool) {
        return { [fieldName]: convertYesNoToBoolean(value) };
    }

    if (value === null && type === Constants.FieldDefinitionFieldTextTypes.text) {
        return { [fieldName]: '' };
    }

    return { [fieldName]: value };
};

const getChangeValue = (currentValue: any, previousValue: any, colSum: ColumnSummary, type: string) => {
    if (currentValue === null && previousValue === null) return null;

    if (isStringNumeric(currentValue))
        return isStringNumeric(colSum.change) ? getCellNumberFromString(colSum.change, type, true) : colSum.change;

    if (type === FieldDefinitionFieldType.BOOL || type === FieldDefinitionFieldType.TEXT) return '';

    return null;
};

export const getSiteOverviewData = (siteTable: SiteRow[]): Record<string, SiteRow>[] => {
    const convertedData: Record<string, SiteRow>[] = [];

    siteTable?.forEach((siteRow: SiteRow) => {
        const convertedRow: Record<string, any> = {
            siteName: siteRow.siteName,
        };

        siteRow.columnSummary?.forEach((colSum) => {
            const { type, title } = colSum;
            const currentColObject = !isSplittedValue(colSum.current)
                ? getFieldData(colSum, 'current', type)
                : {
                      current: getCellNumberFromString(colSum.current.split('|')[1], type),
                      currentStatus: colSum.current.split('|')[2] ?? '',
                  };

            const previousColObject = !isSplittedValue(colSum.previous)
                ? getFieldData(colSum, 'previous', type)
                : {
                      previous: getCellNumberFromString(colSum.previous.split('|')[1], type),
                      previousStatus: colSum.previous.split('|')[2] ?? '',
                  };
            convertedRow[`colSummary${title}`] = {
                ...currentColObject,
                ...previousColObject,
                change: getChangeValue(currentColObject.current, previousColObject.previous, colSum, type),
                title,
                movement: getChangeMovement(previousColObject.previous, currentColObject.current, colSum.change, type),
                order: colSum.columnOrder,
                type,
            };
        });

        siteRow.panelSummary?.forEach((panelSum) => {
            const panelsData: Record<string, any> = {
                plotTile: panelSum.plotTile,
                order: siteRow.panelColumnOrder,
            };

            panelSum.values?.forEach((val) => {
                const { type, title } = val;
                const currentPanelObject = !isSplittedValue(val?.current)
                    ? {
                          ...(val?.current != null
                              ? {
                                    current: isStringNumeric(val.current)
                                        ? getCellNumberFromString(val.current, type)
                                        : val.current,
                                }
                              : {}),
                      }
                    : {
                          current: getCellNumberFromString(val.current?.split('|')[1], type),
                          currentStatus: val.current?.split('|')[2] ?? '',
                      };

                const previousPanelObject = !isSplittedValue(val?.previous)
                    ? {
                          ...(val?.previous != null
                              ? {
                                    previous: isStringNumeric(val.previous)
                                        ? getCellNumberFromString(val.previous, type)
                                        : val.previous,
                                }
                              : {}),
                      }
                    : {
                          previous: getCellNumberFromString(val?.previous?.split('|')[1], type),
                          previousStatus: val?.previous?.split('|')[2] ?? '',
                      };

                panelsData[`panelValues${title}`] = {
                    ...currentPanelObject,
                    ...previousPanelObject,
                    ...(val?.change != null ? { change: getCellNumberFromString(val.change, type, true) } : {}),
                    title,
                    type,
                    movement: getChangeMovement(
                        previousPanelObject?.previous,
                        currentPanelObject?.current,
                        val?.change,
                        type,
                    ),
                };
            });

            convertedRow[`panelSummary${panelSum?.panelName}`] = panelsData;
        });

        convertedData.push(convertedRow);
    });

    return convertedData;
};

const getColumnAdditionalProps = (
    siteRows: Record<string, SiteRow>[],
    key: string,
    fieldValue: string,
    fieldStatus: string,
) => {
    const renderCell = (path: string, dataItem: any) => getObjValueByPath(path, dataItem);
    const firstRow = siteRows[0];
    const isColumnWithStatus = siteRows.find((row) => !!row?.[key]?.[fieldStatus]);
    const { type } = firstRow[key];

    if ([Constants.FieldDefinitionFieldTextTypes.int, Constants.FieldDefinitionFieldTextTypes.float].includes(type)) {
        if (!isColumnWithStatus) {
            return {
                cells: {
                    data: CustomizableCell((dataItem: any) =>
                        getObjValueByPath(`${key}.${fieldValue}`, dataItem, type),
                    ),
                },
            };
        }
        return {
            cells: { data: SplittedValuesCell(`${key}.${fieldValue}`, `${key}.${fieldStatus}`) },
        };
    }

    if (type === Constants.FieldDefinitionFieldTextTypes.bool) {
        return {
            cells: { data: (params: any) => YesNoCell({ ...params, renderCell }) },
            filterUi: GridColumnMenuActiveFilter,
        };
    }

    if (
        [Constants.FieldDefinitionFieldTextTypes.date, Constants.FieldDefinitionFieldTextTypes.partialDate].includes(
            type,
        )
    ) {
        return {
            cells: { data: (params: any) => DateCell({ ...params, renderCell }) },
            filterUi: GridColumnDateFilter,
        };
    }

    return {};
};

export const getSiteOverviewColumns = (
    siteRow: Record<string, SiteRow>[],
    previousDataSet: IExtendedDataSetFields,
    currentDataSet: IExtendedDataSetFields | null | undefined,
    translate: (value: string) => string,
) => {
    const cols: IExpandableGridColumnV2[] = [
        {
            label: translate('cm:site'),
            id: 'site-name',
            field: 'siteName',
            minWidth: 100,
            lockedByDefault: true,
            show: true,
            cells: { data: MaxLengthCell(150) },
            order: 0,
        },
    ];
    const firstRow = siteRow[0];
    const prevDatasetTooltip = previousDataSet?.isDefault
        ? translate('cm:noCdSelected')
        : previousDataSet?._dataCutDate;
    const prevDatasetTitle = previousDataSet?.isDefault ? ' ' : previousDataSet?._dataCutDate;

    if (firstRow)
        Object.keys(firstRow).forEach((key) => {
            if (Object.prototype.hasOwnProperty.call(firstRow, key)) {
                const childKeyBase = `${key}-child`;
                const filter = getFilterType(firstRow[key]?.type || '');

                if (key.startsWith('colSummary')) {
                    cols.push({
                        label: firstRow[key].title,
                        id: `column-title-${key}`,
                        field: key,
                        headerClass: 'parent',
                        minWidth: 250,
                        notFilterable: true,
                        notSortable: true,
                        order: firstRow[key].order || 9999,
                        show: true,
                        children: [
                            {
                                field: `${key}.current`,
                                id: `${childKeyBase}-current`,
                                title: translate('cm:selectedDatasetAbbreviation'),
                                tooltipValue: currentDataSet?._dataCutDate,
                                groupTitle: `${firstRow[key].title} - ${currentDataSet?._dataCutDate}`,
                                filter,
                                ...getColumnAdditionalProps(siteRow, key, 'current', 'currentStatus'),
                                isChild: true,
                                minWidth: 90,
                                show: true,
                            },
                            {
                                field: `${key}.previous`,
                                id: `${childKeyBase}-previous`,
                                title: translate('cm:comparisonDatasetAbbreviation'),
                                tooltipValue: prevDatasetTooltip,
                                groupTitle: `${firstRow[key].title} - ${prevDatasetTitle}`,
                                filter,
                                ...getColumnAdditionalProps(siteRow, key, 'previous', 'previousStatus'),
                                isChild: true,
                                minWidth: 90,
                                show: true,
                            },
                            {
                                field: `${key}.change`,
                                id: `${childKeyBase}-change`,
                                title: translate('cm:changeAbbreviation'),
                                tooltipValue: translate('ra:change'),
                                groupTitle: `${firstRow[key].title} - ${translate('ra:change')}`,
                                filter,
                                isChild: true,
                                cells: { data: MovementCell(`${key}.movement`, true) },
                                minWidth: 90,
                                show: true,
                            },
                        ],
                    });
                }

                if (key.startsWith('panelSummary')) {
                    const panels: IExpandableGridColumnV2[] = [];
                    Object.keys(firstRow[key]).forEach((keyPanel) => {
                        if (keyPanel !== 'plotTile' && keyPanel !== 'order') {
                            const isColumnWithStatus = siteRow.find((row) => !!row?.[key]?.[keyPanel]?.currentStatus);
                            panels.push({
                                title: firstRow[key][keyPanel].title,
                                groupTitle: `${firstRow[key].plotTile} - ${firstRow[key][keyPanel].title}`,
                                field: `${key}.${keyPanel}`,
                                id: `${key}-${keyPanel}`,
                                minWidth: 250,
                                headerClass: 'parent',
                                notSortable: true,
                                notFilterable: true,
                                hideColumnLock: true,
                                isChild: true,
                                show: true,
                                children: [
                                    {
                                        field: `${key}.${keyPanel}.current`,
                                        id: `${childKeyBase}-${keyPanel}-current`,
                                        title: translate('cm:selectedDatasetAbbreviation'),
                                        tooltipValue: currentDataSet?._dataCutDate || '',
                                        groupTitle: `${firstRow[key].plotTile} - ${firstRow[key][keyPanel].title} - ${currentDataSet?._dataCutDate}`,
                                        filter: 'numeric',
                                        cells: {
                                            data: !isColumnWithStatus
                                                ? CustomizableCell((dataItem: any) =>
                                                      getObjValueByPath(`${key}.${keyPanel}.current`, dataItem),
                                                  )
                                                : SplittedValuesCell(
                                                      `${key}.${keyPanel}.current`,
                                                      `${key}.${keyPanel}.currentStatus`,
                                                  ),
                                        },
                                        show: true,
                                        minWidth: 90,
                                        isChild: true,
                                        hideColumnLock: true,
                                    },
                                    {
                                        field: `${key}.${keyPanel}.previous`,
                                        id: `${childKeyBase}-${keyPanel}-previous`,
                                        title: translate('cm:comparisonDatasetAbbreviation'),
                                        tooltipValue: prevDatasetTooltip,
                                        groupTitle: `${firstRow[key].plotTile} - ${firstRow[key][keyPanel].title} - ${prevDatasetTitle}`,
                                        filter: 'numeric',
                                        cells: {
                                            data: !isColumnWithStatus
                                                ? CustomizableCell((dataItem: any) =>
                                                      getObjValueByPath(`${key}.${keyPanel}.previous`, dataItem),
                                                  )
                                                : SplittedValuesCell(
                                                      `${key}.${keyPanel}.previous`,
                                                      `${key}.${keyPanel}.previousStatus`,
                                                  ),
                                        },
                                        show: true,
                                        isChild: true,
                                        hideColumnLock: true,
                                        minWidth: 90,
                                    },
                                    {
                                        field: `${key}.${keyPanel}.change`,
                                        id: `${childKeyBase}-${keyPanel}-change`,
                                        title: translate('cm:changeAbbreviation'),
                                        tooltipValue: translate('ra:change') || '',
                                        groupTitle: `${firstRow[key].plotTile} - ${
                                            firstRow[key][keyPanel].title
                                        } - ${translate('ra:change')}`,
                                        filter: 'numeric',
                                        show: true,
                                        isChild: true,
                                        hideColumnLock: true,
                                        cells: { data: MovementCell(`${key}.${keyPanel}.movement`, true) },
                                        minWidth: 90,
                                    },
                                ],
                            });
                        }
                    });

                    cols.push({
                        label: firstRow[key].plotTile,
                        id: `panel-title-${key}`,
                        field: key,
                        headerClass: 'parent',
                        notSortable: true,
                        notFilterable: true,
                        hideColumnLock: true,
                        minWidth: 300,
                        children: panels,
                        order: firstRow[key].order,
                        show: true,
                    });
                }
            }
        });

    return cols;
};
